import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Store, Layers, Compass, ClipboardCheck, ShieldCheck,
  Cog, Rocket, Scale, Globe2,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.2, 0.7, 0.2, 1] } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };

// Each tile answers "why does this elevate HidroVerse above the competition?"
// Plain, formal, factual. No counts, no poetic phrasing.
const POINTS = [
  {
    icon: Store,
    title: "Retail and wholesale division.",
    body: "HidroVerse runs a public retail store and a national wholesale supply business alongside the installs we deliver. That structure gives us superior stockholding and product reach, which translates into faster response times and minimum downtime on every project. While other contractors phone their suppliers for solutions, we operate as one of the suppliers — placing us in the advisory role for the rest of the industry. The exposure deepens our product knowledge and the range of sites we work across the country.",
    accent: "#1FB6C1",
  },
  {
    icon: Layers,
    title: "Full water-services coverage.",
    body: "Purification, wastewater treatment, pumps and pressure, source and storage, pool and spa, irrigation, monitoring and telemetry, rentals, and service — all delivered in-house under one HidroVerse accountability. The breadth lets us specify, integrate and commission an entire wet-services scope under a single contract, and lets every discipline carry the technical context of the others.",
    accent: "#0C2A40",
  },
  {
    icon: Compass,
    title: "Consulting role to engineering firms.",
    body: "Multi-disciplinary engineering firms, architects and master planners route their water-side specification through HidroVerse. The advisory work we deliver to other professionals raises the technical standard we bring to every install, and gives our clients access to expertise that is otherwise reserved for the consulting brief.",
    accent: "#1D6A8C",
  },
  {
    icon: ClipboardCheck,
    title: "End-to-end wet-services scope.",
    body: "From feed-water audit through reticulation design, civil briefing, commissioning, validation and operator training, the entire water side of a project sits under a single HidroVerse accountability. The client signs one contract, deals with one project lead, and receives one signed-off compliance pack at handover.",
    accent: "#42954B",
  },
  {
    icon: ShieldCheck,
    title: "Service agreements on any plant.",
    body: "HidroVerse provides documented service-level agreements on the plants we install, and on existing plants where expert oversight is required. A client who inherited a non-compliant or under-performing system from a previous contractor does not have to live with that decision — we bring it back into compliance and keep it there.",
    accent: "#1FB6C1",
  },
  {
    icon: Cog,
    title: "Designed to the site. Commissioned to specification.",
    body: "Every system is sized and specified against the site's actual feed-water chemistry, peak demand profile and civil constraints — then commissioned against the original design rather than a brochure rate. The same engineering discipline applies whether the install is a domestic borehole pack or a multi-megalitre municipal plant. The result is a plant that performs on day one and continues to perform years later.",
    accent: "#0C2A40",
  },
  {
    icon: Rocket,
    title: "Active research and development.",
    body: "The retail and wholesale division exposes HidroVerse to sites across the entire country — far beyond the projects we install ourselves. That breadth of visibility makes us acutely aware of the shortcomings and gaps in the industry. We actively pursue research and development as pioneers, both standalone and alongside industry leaders, to engineer new products and solutions that move the industry forward.",
    accent: "#1D6A8C",
  },
  {
    icon: Scale,
    title: "Specification-led. Brand-independent.",
    body: "HidroVerse selects components on the basis of duty, not distributor margin or stocked-brand convenience. The same wholesale and retail reach that puts us in front of the country's water-product range gives us the working knowledge to specify the correct equipment for the duty — and the independence to recommend it without being bound to a single brand or supplier.",
    accent: "#42954B",
  },
  {
    icon: Globe2,
    title: "Cross-border project capability.",
    body: "HidroVerse delivers projects across Mpumalanga, Limpopo, Eswatini and Mozambique — including Karingani Game Reserve in the M'Lambane Conservation Initiative. The same compliance discipline, documentation standard and post-install service applies regardless of which border the parts and the team cross.",
    accent: "#1FB6C1",
  },
];

export default function WhatSetsUsApart() {
  return (
    <section
      className="px-6 md:px-12 lg:px-20 pt-4 md:pt-6 pb-12 md:pb-16"
      style={{ background: "#FAFAF7", color: "#0C2A40" }}
      data-testid="what-sets-us-apart"
    >
      <div className="max-w-[1500px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-8 md:mb-10">
          <div className="max-w-[60ch]">
            <h2
              className="font-display"
              style={{
                fontSize: "clamp(2rem, 4.5vw, 3.4rem)",
                lineHeight: 1.06,
                letterSpacing: "-0.02em",
                fontWeight: 700,
                color: "#0C2A40",
              }}
            >
              Why HidroVerse<br />
              <span style={{ color: "#1FB6C1" }}>is your strongest choice.</span>
            </h2>
          </div>
          <Link
            to="/contact"
            className="text-[0.78rem] font-mono uppercase inline-flex items-center gap-1.5 transition-colors hover:text-[#1FB6C1]"
            style={{ letterSpacing: "0.18em", color: "#0C2A40" }}
            data-testid="wsa-cta"
          >
            Talk to our team <span aria-hidden>→</span>
          </Link>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4"
        >
          {POINTS.map((p) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                variants={fadeUp}
                className="relative p-5 md:p-5 transition-transform duration-300 hover:-translate-y-1"
                style={{
                  background: "#FFFFFF",
                  borderRadius: 6,
                  border: "1px solid rgba(12,42,64,0.08)",
                  boxShadow: "0 1px 0 rgba(12,42,64,0.02)",
                }}
                data-testid={`wsa-point-${p.title.split(" ").join("-").toLowerCase().replace(/[^a-z-]/g, "").slice(0, 24)}`}
              >
                <span
                  className="absolute left-0 top-0 bottom-0 w-[3px]"
                  style={{ background: p.accent }}
                  aria-hidden
                />
                <div
                  className="inline-flex items-center justify-center mb-3"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 7,
                    background: `${p.accent}14`,
                    color: p.accent,
                  }}
                  aria-hidden
                >
                  <Icon size={17} />
                </div>
                <h3
                  className="font-display mb-2"
                  style={{
                    fontSize: "0.95rem",
                    lineHeight: 1.28,
                    letterSpacing: "-0.005em",
                    fontWeight: 600,
                    color: "#0C2A40",
                  }}
                >
                  {p.title}
                </h3>
                <p
                  className="font-light"
                  style={{
                    fontSize: "0.84rem",
                    lineHeight: 1.55,
                    color: "rgba(26,31,37,0.78)",
                  }}
                >
                  {p.body}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
